import { Form, useLoaderData, useSearchParams } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import debounce from 'lodash.debounce';
import { useRef, useState, useMemo } from 'react';
import { track } from '~/services/analytics/index.client';
import { validateSearchQuery } from '~/utils/validations.ts';
import { getTranslations } from '~/services/localization';
import { PreloadImage } from '~/components/preload-image';
import { Underline } from '~/components/underline';
import { useSearchContext } from '~/contexts/SearchContext.tsx';
import type { SearchFilterEntry } from '~/services/router/index.server';
import type { FeedEntry } from '~/services/layout';
import { twMerge } from 'tailwind-merge';
import { useSearchFiltersResponsiveValues } from './use-search-filters-responsive-values';
import {
  getSearchFilterTabItemSizingClassName,
  getSearchFilterTabItemsContainerClassName,
  getSearchFilterTabItemsAlignmentClassName,
  getSearchFilterTabItemInnerAlignmentClassName,
  getSearchFilterBorderStyleClassName,
} from '../utils/get-search-filter-tab-sizing';
import { SearchFiltersArrowButton } from './search-filters-arrow-button';
import { useTabScrollObserver } from '~/services/plugins/quick-brick-tabs/hooks';
import { triggerTabScrollAction } from '~/services/plugins/quick-brick-tabs/utils/trigger-tab-scroll-action';
import { ButtonCell } from '~/services/plugins/button_cell/components';

/**
 * SearchV2 Screen
 * Plugin identifier: search_v2
 */
export function SearchV2(): JSX.Element {
  const {
    searchScreenId,
    pathname,
    autofocusEnabled,
    q,
    filterTag,
    searchScreenStyles,
    onChange,
    resetSearchParams,
    showCancelIconButton,
    inputRef,
    honeypotInputRef,
    translations,
    isSearching,
    enableSearchFilters,
    filtersFeedSource,
    filterEntries,
    onFilterSelect,
  } = useSearch();

  const searchIconSwitch: boolean = searchScreenStyles?.search_icon_switch;
  const searchIconFocusedSwitch: boolean =
    searchScreenStyles?.search_icon_focused_switch;

  const searchPlaceholder: string = translations?.placeholder_message || '';

  const clearIconHoverSwitch: boolean =
    searchScreenStyles?.clear_icon_hover_switch;

  const normalizedPathname: string = useMemo(() => {
    try {
      const parsedPathname = new URL(pathname, 'http://localhost');
      return `${parsedPathname.pathname}${parsedPathname.search}${parsedPathname.hash}`;
    } catch {
      if (pathname.startsWith('/')) return pathname;
      return `/${pathname}`;
    }
  }, [pathname]);

  const showFilters: boolean =
    Boolean(enableSearchFilters) &&
    Boolean(filtersFeedSource) &&
    Array.isArray(filterEntries) &&
    filterEntries.length > 0;

  const {
    stackingSwitch,
    tabItemSize,
    tabsItemAlignment,
    tabItemInnerAlignment,
    borderParentContainer,
    borderContainer,
    parentShadowSwitch,
    shadowSwitch,
    parentBackgroundBlurSwitch,
    backgroundBlurSwitch,
    maxWidthSwitch,
    blendingSwitch,
    arrowsSwitch,
    horizontalGutter,
    paddingLeft,
    paddingRight,
    componentMaxWidth,
  } = useSearchFiltersResponsiveValues(searchScreenStyles ?? {});

  const filterCellIds = useMemo(
    () => [
      searchScreenStyles?.search_filters_button_cell_plugin_configuration_id_mobile as string,
      searchScreenStyles?.search_filters_button_cell_plugin_configuration_id_tablet as string,
      searchScreenStyles?.search_filters_button_cell_plugin_configuration_id_desktop as string,
      searchScreenStyles?.search_filters_button_cell_plugin_configuration_id_large_desktop as string,
    ],
    [searchScreenStyles]
  );

  const filterListRef = useRef<HTMLDivElement>(null);

  const { canScrollLeft, canScrollRight } = useTabScrollObserver(filterListRef);

  const scrollFilters = (direction: 'left' | 'right'): void => {
    triggerTabScrollAction({
      ref: filterListRef,
      gutter: horizontalGutter ?? 0,
      paddingLeft: paddingLeft ?? 0,
      paddingRight: paddingRight ?? 0,
      componentMaxWidth,
      tabItemSize: (tabItemSize as string) ?? 'dynamic',
      direction,
    });
  };

  const filterArrowsScrollClass = useMemo(
    () => (arrowsSwitch ? 'search-filters-arrows-scroll-area' : ''),
    [arrowsSwitch]
  );

  const filterTabItemSizingClassName = useMemo(
    () =>
      getSearchFilterTabItemSizingClassName(
        (tabItemSize as string) ?? 'dynamic'
      ),
    [tabItemSize]
  );
  const filterTabItemsContainerClassName = useMemo(
    () =>
      getSearchFilterTabItemsContainerClassName(
        (tabItemSize as string) ?? 'dynamic'
      ),
    [tabItemSize]
  );
  const filterTabItemsAlignmentClassName = useMemo(
    () =>
      getSearchFilterTabItemsAlignmentClassName(
        (tabsItemAlignment as string) ?? 'left'
      ),
    [tabsItemAlignment]
  );
  const filterTabItemInnerAlignmentClassName = useMemo(
    () =>
      getSearchFilterTabItemInnerAlignmentClassName(
        (tabItemInnerAlignment as string) ?? 'center'
      ),
    [tabItemInnerAlignment]
  );
  const filterParentBorderClassName = useMemo(
    () =>
      getSearchFilterBorderStyleClassName(
        'search-filters-parent-container-border',
        (borderParentContainer as string) ?? 'bottom'
      ),
    [borderParentContainer]
  );
  const filterContainerBorderClassName = useMemo(
    () =>
      getSearchFilterBorderStyleClassName(
        'search-filters-container-border',
        (borderContainer as string) ?? 'bottom'
      ),
    [borderContainer]
  );

  const filterOverflowClasses = useMemo(
    () =>
      stackingSwitch
        ? 'overflow-y-auto overflow-x-hidden flex flex-wrap'
        : 'overflow-y-hidden overflow-x-auto flex-nowrap',
    [stackingSwitch]
  );

  const filterParentClassName = useMemo(
    () =>
      twMerge(
        'search-filters-parent-classes search-filters-parent-corner-radius w-full',
        filterParentBorderClassName,
        parentShadowSwitch && 'search-filters-parent-shadow',
        parentBackgroundBlurSwitch && 'search-filters-parent-backdrop-filter'
      ),
    [
      filterParentBorderClassName,
      parentShadowSwitch,
      parentBackgroundBlurSwitch,
    ]
  );

  const filterContainerClassName = useMemo(
    () =>
      twMerge(
        'search-filters-container-classes search-filters-container-corner-radius',
        'search-filters-container-horizontal-gutter',
        stackingSwitch && 'search-filters-container-vertical-gutter',
        'scrollbar-hide w-full',
        filterOverflowClasses,
        filterTabItemsContainerClassName,
        filterTabItemsAlignmentClassName,
        filterContainerBorderClassName,
        maxWidthSwitch && 'search-filters-container-max-width',
        shadowSwitch && 'search-filters-container-shadow',
        backgroundBlurSwitch && 'search-filters-container-backdrop-filter'
      ),
    [
      stackingSwitch,
      filterOverflowClasses,
      filterTabItemsContainerClassName,
      filterTabItemsAlignmentClassName,
      filterContainerBorderClassName,
      maxWidthSwitch,
      shadowSwitch,
      backgroundBlurSwitch,
    ]
  );

  const filterButtons = filterEntries.map((entry: SearchFilterEntry) => {
    const tag = entry.extensions?.filterTag ?? entry.title?.toLowerCase();
    const isSelected =
      filterTag === tag || (filterTag == null && tag === 'all');
    return (
      <div
        key={entry.id}
        role="tab"
        aria-selected={isSelected}
        className={twMerge(
          'search-filters-tab-classes min-w-0 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap',
          filterTabItemSizingClassName,
          filterTabItemInnerAlignmentClassName
        )}
        onClick={() => onFilterSelect(tag)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onFilterSelect(tag);
          }
        }}
        tabIndex={isSelected ? 0 : -1}
      >
        <ButtonCell
          selected={isSelected}
          cellIds={filterCellIds}
          entry={entry as FeedEntry}
        />
      </div>
    );
  });

  const filterContent = stackingSwitch ? (
    <div
      ref={filterListRef}
      className={filterContainerClassName}
      role="tablist"
      aria-label="Search filters"
    >
      {filterButtons}
    </div>
  ) : (
    <div
      className={twMerge(
        'relative w-full',
        maxWidthSwitch && 'search-filters-container-max-width',
        shadowSwitch && 'search-filters-container-shadow'
      )}
    >
      {arrowsSwitch && canScrollLeft && (
        <SearchFiltersArrowButton
          direction="left"
          onClick={() => scrollFilters('left')}
          show={canScrollLeft}
          styles={searchScreenStyles}
          searchScreenId={searchScreenId}
        />
      )}
      <div
        ref={filterListRef}
        className={twMerge(filterContainerClassName, filterArrowsScrollClass)}
        role="tablist"
        aria-label="Search filters"
      >
        {filterButtons}
      </div>
      {arrowsSwitch && canScrollRight && (
        <SearchFiltersArrowButton
          direction="right"
          onClick={() => scrollFilters('right')}
          show={canScrollRight}
          styles={searchScreenStyles}
          searchScreenId={searchScreenId}
        />
      )}
    </div>
  );

  return (
    <section
      role="search"
      aria-label="Search Component"
      className={`search-v2-${searchScreenId} search-bar-container-classes inset-x-0 z-40`}
    >
      <Form method="get" action={normalizedPathname}>
        {enableSearchFilters && filterTag != null && filterTag !== '' && (
          <input type="hidden" name="filterTag" value={filterTag} />
        )}
        <div className="group relative">
          {searchIconFocusedSwitch && (
            <PreloadImage tailwindImageClass="search-icon-focused-asset" />
          )}

          <div className="absolute inset-y-0 left-0 flex items-center bg-cover bg-center">
            {searchIconSwitch && (
              <div
                className={`search-icon-classes bg-search-icon-asset bg-contain bg-center bg-no-repeat ${
                  searchIconFocusedSwitch
                    ? 'group-focus-within:bg-search-icon-focused-asset'
                    : ''
                }`}
                aria-label="search icon"
              />
            )}
          </div>

          {clearIconHoverSwitch && (
            <PreloadImage tailwindImageClass="search-clear-icon-asset" />
          )}

          {clearIconHoverSwitch && (
            <PreloadImage tailwindImageClass="search-clear_icon_hover_asset" />
          )}

          {isSearching && (
            <div
              className="search-clear-icon-container-classes absolute inset-y-0 right-0 flex h-full items-center"
              aria-label="Loading search results"
            >
              <div className="border-search-color search-clear-icon-classes animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          )}
          {showCancelIconButton && !isSearching && (
            <div
              className={`search-clear-icon-container-classes absolute inset-y-0 right-0 flex h-full items-center bg-transparent bg-cover bg-center`}
            >
              <div
                className={`search-clear-icon-classes cursor-pointer bg-search-clear-icon-asset bg-contain bg-center bg-no-repeat ${
                  clearIconHoverSwitch
                    ? 'hover:bg-search-clear_icon_hover_asset'
                    : ''
                }`}
                onClick={resetSearchParams}
                aria-label="cancel search and clear input"
                role="cancel"
              />
            </div>
          )}
          <input
            ref={honeypotInputRef}
            className="honeypot"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            name="name"
          />
          <input
            ref={inputRef}
            autoFocus={autofocusEnabled}
            name="q"
            key={q}
            defaultValue={q}
            type="search"
            id="default-search"
            className={`
              group
              search-bar-classes
              search-label-classes
              w-full
              border-search_bar_border_color
              bg-search_bar_background_color
              font-search_label_font_family
              text-search-color
              caret-search-bar search-label-transform 
              placeholder:text-search-placeholder-color
              focus:border-search_bar_focused_border_color
              focus:bg-search_bar_focused_background_color
              focus:text-search-focused-color
              focus:outline-none
              `}
            placeholder={searchPlaceholder}
            onChange={onChange}
            autoComplete="off"
            onKeyDown={(e) => {
              const loadFallbackScreenOnEnter: boolean =
                e.key === 'Enter' &&
                inputRef?.current?.value === '' &&
                q !== '';

              if (!loadFallbackScreenOnEnter) return;

              resetSearchParams();
            }}
          />
          <Underline classNames="search-bar-underline-classes absolute bottom-0 left-0 right-0 top-0 bg-search_bar_underline_bottom_color group-focus-within:bg-search_bar_focused_underline_bottom_color" />
        </div>
        {showFilters && (
          <div className={filterParentClassName}>
            {blendingSwitch ? (
              <div className={twMerge('search-filters-mask-blending')}>
                {filterContent}
              </div>
            ) : (
              filterContent
            )}
          </div>
        )}
      </Form>
    </section>
  );
}

function useSearch(): {
  searchScreenId: string;
  pathname: string;
  autofocusEnabled: boolean;
  q: string;
  filterTag: string | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetSearchParams: () => void;
  showCancelIconButton: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  honeypotInputRef: React.RefObject<HTMLInputElement>;
  searchScreenStyles: any;
  translations: any;
  isSearching: boolean;
  enableSearchFilters: boolean | undefined;
  filtersFeedSource: string | undefined;
  filterEntries: SearchFilterEntry[];
  onFilterSelect: (tag: string) => void;
} {
  const { searchData, analyticsTrackData } = useLoaderData<typeof rootLoader>();
  const [searchParams] = useSearchParams();
  const { triggerSearch, resetSearch, isSearching } = useSearchContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const honeypotInputRef = useRef<HTMLInputElement>(null);
  const hasReachedMinLengthRef = useRef<boolean>(false);

  const [showCancelIconButton, setShowCancelIconButton] = useState<boolean>(
    !!searchParams.get('q')
  );

  const {
    searchScreenStyles,
    localizations,
    minimumQueryLength,
    maximumQueryLength,
    searchScreenId,
    pathname,
    autofocusEnabled,
    q,
    filterTag,
    enableSearchFilters,
    filtersFeedSource,
    debounce: searchInputDebounce,
    filterEntries: loaderFilterEntries,
  } = searchData as any;

  const filterEntries: SearchFilterEntry[] = Array.isArray(loaderFilterEntries)
    ? loaderFilterEntries
    : [];

  const translations = getTranslations(localizations);

  const [activeFilterTag, setActiveFilterTag] = useState<string | null>(
    filterTag ?? null
  );

  const onChange = debounce((event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value;
    if (value) setShowCancelIconButton(true);

    if (honeypotInputRef.current?.value !== '') return;
    if (value.length >= minimumQueryLength) {
      hasReachedMinLengthRef.current = true;
    }
    if (value.length === 0 && hasReachedMinLengthRef.current) {
      resetSearchParams();
      return;
    }
    if (!validateSearchQuery(value)) return;
    if (value.length < minimumQueryLength || value.length > maximumQueryLength)
      return;

    track('search_executed', {
      ...analyticsTrackData,
      query_length: value?.length,
      query_text: value,
    });

    triggerSearch(value, enableSearchFilters ? activeFilterTag : undefined);
  }, searchInputDebounce as number | undefined);

  const onFilterSelect = (tag: string): void => {
    const newTag = tag === 'all' ? null : tag;
    setActiveFilterTag(newTag);
    const currentQ =
      inputRef.current?.value ?? q ?? searchParams.get('q') ?? '';
    triggerSearch(currentQ, newTag);
  };

  const resetSearchParams = (): void => {
    if (!inputRef.current) return;
    track('search_canceled', {
      ...analyticsTrackData,
      query_length: inputRef.current.value?.length,
      query_text: inputRef.current.value,
    });
    inputRef.current.value = '';
    inputRef.current.focus();
    setShowCancelIconButton(false);
    setActiveFilterTag(null);
    hasReachedMinLengthRef.current = false;
    resetSearch();
  };

  return {
    searchScreenId,
    pathname,
    autofocusEnabled,
    q,
    filterTag: enableSearchFilters ? activeFilterTag : filterTag ?? null,
    onChange,
    resetSearchParams,
    showCancelIconButton,
    inputRef,
    honeypotInputRef,
    searchScreenStyles,
    translations,
    isSearching,
    enableSearchFilters,
    filtersFeedSource,
    filterEntries,
    onFilterSelect,
  };
}
